'use client'

import { SplineScene } from "@/components/ui/splite";
import { Card } from "@/components/ui/card"
import { Spotlight } from "@/components/ui/spotlight"
import { GitHubSignInButton } from '../common/GitHubSignInButton';
import { TypewriterText } from "@/components/ui/TypewriterText";
import { motion } from "@/components/ui/motion-provider";
import { useMediaQuery } from "@/hooks/use-media-query";
import { Typography } from "@/components/ui/typography";
import { cn } from "@/lib/utils";

const techRoles = [
  "Software Engineer",
  "Full Stack Developer",
  "Backend Developer",
  "Frontend Developer",
  "DevOps Engineer",
  "Data Engineer",
  "ML Engineer"
];

export function HeroSection() {
  const isMobile = useMediaQuery("(max-width: 768px)");
  const isSmallMobile = useMediaQuery("(max-width: 480px)");

  return (
    <div className="py-2 sm:py-4 md:py-8">
      <div className="px-2 sm:px-4 md:px-6 lg:px-8">
        <Card className="w-full min-h-[500px] xs:min-h-[550px] sm:min-h-[500px] md:min-h-[550px] bg-white dark:bg-[#0A0A0A] relative overflow-hidden border-0 rounded-[1.25rem] sm:rounded-[1.5rem]">
          {/* Enhanced gradient overlay */}
          <div 
            className="absolute inset-0 bg-[radial-gradient(ellipse_80%_80%_at_50%_-20%,rgba(124,58,237,0.05),rgba(255,255,255,0))]
                     dark:bg-[radial-gradient(ellipse_80%_80%_at_50%_-20%,rgba(124,58,237,0.15),rgba(0,0,0,0))]"
          />
          
          {/* Desktop cursor spotlight */}
          {!isMobile && (
            <Spotlight
              className="-top-20 left-0 md:left-60 md:-top-20 text-violet-500/30 md:text-violet-500/40"
              size={400}
            />
          )}

          {/* Mobile static spotlight behind robot */}
          {isMobile && (
            <>
              <div 
                className={cn(
                  "absolute left-1/2 -translate-x-1/2",
                  "w-[300px] xs:w-[350px] sm:w-[400px]",
                  "h-[200px] xs:h-[220px] sm:h-[250px]",
                  "top-0",
                  "rounded-b-full bg-violet-600/20 blur-[80px]",
                  "pointer-events-none"
                )}
              />
              <div 
                className={cn(
                  "absolute left-1/2 -translate-x-1/2",
                  "w-[250px] xs:w-[300px] sm:w-[350px]",
                  "h-[150px] xs:h-[180px] sm:h-[200px]",
                  "top-[20px]",
                  "rounded-b-full bg-purple-500/25 blur-[60px]",
                  "pointer-events-none"
                )}
              />
            </>
          )}
          
          <div className="flex flex-col md:flex-row h-full">
            {/* Right content - Moved to top on mobile */}
            <motion.div 
              className={cn(
                "relative",
                isMobile ? 'order-first -mb-12 xs:-mb-16 sm:-mb-20' : 'order-last',
                isMobile ? 'w-full h-[200px] xs:h-[250px] sm:h-[300px]' : 'flex-1 min-h-[450px]',
                "md:min-h-0 md:absolute md:right-[5%] md:top-[12%] md:bottom-0 md:w-[42%]"
              )}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.3, duration: 0.5 }}
            >
              <SplineScene 
                scene="https://prod.spline.design/kZDDjO5HuC9GJUM2/scene.splinecode"
                className={cn(
                  "w-full h-full object-contain",
                  isSmallMobile ? 'scale-[1]' :
                  isMobile ? 'scale-[1.1]' : 'scale-[1.05]',
                  "origin-center transform-gpu opacity-90",
                  "relative z-10"
                )}
              />
              {isMobile && (
                <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0A] via-transparent to-transparent pointer-events-none" />
              )}
            </motion.div>

            {/* Left content - Enhanced typography and spacing */}
            <motion.div 
              className={cn(
                "flex-1 relative z-20 flex flex-col",
                isMobile ? 'order-last p-4 xs:p-6 sm:p-8 justify-center items-center text-center' : 'order-first p-8 sm:p-10 md:p-14 justify-center',
                "md:max-w-[65%] md:items-start md:text-left"
              )}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
            >
              <div className={cn(
                "flex flex-col",
                isMobile ? 'items-center gap-y-2 xs:gap-y-3' : 'gap-y-4'
              )}>
                <Typography 
                  variant="h1" 
                  className={cn(
                    "font-poppins text-black dark:text-white font-extrabold tracking-tight leading-[1.1] relative z-20",
                    isSmallMobile ? 'text-4xl' : isMobile ? 'text-5xl sm:text-6xl' : 'text-6xl sm:text-7xl md:text-8xl',
                    "drop-shadow-[0_2px_10px_rgba(0,0,0,0.5)]"
                  )}
                >
                  Find Your Next
                </Typography>
                <div className={cn(
                  "font-poppins font-extrabold tracking-tight leading-[1.1] relative z-20",
                  isSmallMobile ? 'min-h-[1.5em] leading-tight w-full text-3xl' :
                  isMobile ? 'min-h-[1.5em] leading-tight w-full text-4xl sm:text-5xl' : 
                  'min-h-[1.5em] text-5xl sm:text-6xl md:text-7xl'
                )}>
                  <TypewriterText 
                    words={techRoles}
                    className="text-black dark:text-primary drop-shadow-[0_2px_10px_rgba(0,0,0,0.5)]"
                  />
                </div>
              </div>
              
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2, duration: 0.5 }}
                className={cn(
                  "mt-6 xs:mt-8 sm:mt-10 relative z-20",
                  isMobile && "px-2"
                )}
              >
                <Typography 
                  variant="lead"
                  className={cn(
                    "font-inter text-gray-600 dark:text-gray-300 leading-relaxed max-w-[95%] md:max-w-[85%]",
                    isSmallMobile ? 'text-base' :
                    isMobile ? 'text-lg sm:text-xl' : 'text-xl sm:text-2xl',
                    "drop-shadow-[0_1px_2px_rgba(0,0,0,0.5)]"
                  )}
                >
                  Find and connect with skilled developers based on their GitHub activity, contributions, and technical expertise.
                </Typography>
              </motion.div>
              
              <motion.div 
                className={cn(
                  "w-full flex justify-center md:justify-start relative z-20",
                  isSmallMobile ? 'mt-6' : isMobile ? 'mt-8' : 'mt-10'
                )}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4, duration: 0.5 }}
              >
                <GitHubSignInButton
                  variant="hero"
                  className={cn(
                    "font-poppins",
                    isSmallMobile ? "w-full" : "",
                  )}
                />
              </motion.div>
            </motion.div>
          </div>
        </Card>
      </div>
    </div>
  );
}
